import React, { useEffect, useMemo, useState } from 'react';
import {
  // FiChevronDown,
  FiPackage,
  FiSearch,
  FiShield,
  FiX,
} from 'react-icons/fi';
import HelmetHelper from '../../Components/PageHelmet/HelmetHelper.component';
import SideContainerComponent from '../SideContainer.component';
import { MainContentContainer, MainSection } from '../OtherPages.styles';
import ProductHero from './ProductHero';
import {
  ExploreProductsPage,
  CatalogSection,
  CatalogHeader,
  CatalogHeadingGroup,
  CatalogTitle,
  CatalogDescription,
  CatalogControls,
  SearchBox,
  SearchIcon,
  SearchInput,
  ClearSearchButton,
  CategoryFilterWrap,
  CategorySelect,
  // CategoryIcon,
  ProductCount,
  StateCard,
  StateIcon,
  StateTitle,
  StateDescription,
  PaginationControls,
  PageButton,
  PageInfo,
  PageSizeSelect,
} from './ExploreProducts.styles';
import { httpGetAllSCNProducts } from '../../Hooks/products.hooks';
import EachProduct from './EachProduct';
import ContactEnd from './ContactEnd';
import { useSearchParams } from 'react-router-dom';

const ExploreProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);

  // Category now comes directly from the URL.
  // /explore-products = all products
  // /explore-products?category=PPE = PPE products
  const selectedCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    const controller = new AbortController();

    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');

        const response = await httpGetAllSCNProducts();

        if (!response?.ok) {
          throw new Error(
            `Unable to load products. Request returned ${
              response?.status || 'an error'
            }.`
          );
        }

        const data = await response?.json();

        const incomingProducts = Array.isArray(data)
          ? data
          : Array.isArray(data?.products)
            ? data?.products
            : [];

        const dedupeProducts = (list) => {
          const seen = new Map();

          return (list || []).filter((p) => {
            // Prefer stable SKU/productCode before falling back to DB `_id`
            const key =
              p?.productCode ||
              p?.sku ||
              p?._id ||
              p?.id ||
              `${p?.title}-${p?.brand}`;

            if (!key) return false;

            if (seen.has(key)) return false;

            seen.set(key, true);

            return true;
          });
        };

        setProducts(dedupeProducts(incomingProducts));
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        console.error('Unable to load products:', error);

        setErrorMessage(
          error?.message ||
            'We could not load the product catalogue at this time.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();

    return () => controller.abort();
  }, []);

  const categories = useMemo(() => {
    const categoryValues = products
      ?.map((product) => product?.category?.name || product?.category)
      ?.filter(Boolean);

    return [...new Set(categoryValues || [])]?.sort((a, b) =>
      String(a)?.localeCompare(String(b))
    );
  }, [products]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm?.trim()?.toLowerCase();

    return products?.filter((product) => {
      const categoryName = product?.category?.name || product?.category || '';

      const matchesCategory =
        selectedCategory === 'all' ||
        String(categoryName)?.toLowerCase() === selectedCategory?.toLowerCase();

      const searchableValues = [
        product?.name,
        product?.title,
        product?.brand?.name,
        product?.brand,
        product?.sku,
        product?.manufacturerPartNumber,
        product?.shortDescription,
        product?.description,
        categoryName,
        product?.subcategory?.name,
        product?.subcategory,
      ]
        ?.filter(Boolean)
        ?.join(' ')
        ?.toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableValues?.includes(normalizedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [products, searchTerm, selectedCategory]);

  // Return to page 1 whenever the search or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, products]);

  const totalPages = Math.max(
    1,
    Math.ceil((filteredProducts?.length || 0) / pageSize)
  );

  const paginatedProducts = useMemo(() => {
    const list = filteredProducts || [];
    const start = (currentPage - 1) * pageSize;

    return list.slice(start, start + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  // Update the URL whenever the category dropdown changes
  const handleCategoryChange = (category) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (!category || category === 'all') {
      newSearchParams.delete('category');
    } else {
      newSearchParams.set('category', category);
    }

    setSearchParams(newSearchParams);
  };

  // Keep the normal title for the full catalogue.
  // Category links get their own title.
  const catalogTitle =
    selectedCategory === 'all'
      ? 'Explore Safety Products'
      : `${selectedCategory} Products`;

  const catalogDescription =
    selectedCategory === 'all'
      ? 'Search the catalogue or narrow the results by category. Product availability, specifications, and pricing are confirmed directly through our team.'
      : `Browse our ${selectedCategory} products or use the search below to narrow the results. Product availability, specifications, and pricing are confirmed directly through our team.`;

  return (
    <MainContentContainer>
      <HelmetHelper
        title={catalogTitle}
        description={
          selectedCategory === 'all'
            ? 'Explore workplace safety products, PPE, first aid supplies, traffic control products, fire safety equipment, and more.'
            : `Explore ${selectedCategory} products available through our supplier network. Contact our team for product availability, specifications, and pricing.`
        }
      />

      <SideContainerComponent />

      <MainSection>
        <ExploreProductsPage>
          <ProductHero />

          <CatalogSection id="product-catalog">
            <CatalogHeader>
              <CatalogHeadingGroup>
                <CatalogTitle>{catalogTitle}</CatalogTitle>

                <CatalogDescription>{catalogDescription}</CatalogDescription>
              </CatalogHeadingGroup>

              <ProductCount>
                {filteredProducts?.length || 0}{' '}
                {filteredProducts?.length === 1 ? 'product' : 'products'}
              </ProductCount>
            </CatalogHeader>

            <CatalogControls>
              <SearchBox>
                <SearchIcon aria-hidden="true">
                  <FiSearch />
                </SearchIcon>

                <SearchInput
                  type="search"
                  value={searchTerm}
                  onChange={(event) =>
                    setSearchTerm(event?.target?.value || '')
                  }
                  placeholder="Search by product, brand, category, or SKU"
                  aria-label="Search products"
                />

                {searchTerm && (
                  <ClearSearchButton
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear product search"
                  >
                    <FiX />
                  </ClearSearchButton>
                )}
              </SearchBox>

              <CategoryFilterWrap>
                <CategorySelect
                  value={selectedCategory}
                  onChange={(event) =>
                    handleCategoryChange(event?.target?.value || 'all')
                  }
                  aria-label="Filter products by category"
                >
                  <option value="all">All product categories</option>

                  {categories?.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </CategorySelect>
              </CategoryFilterWrap>
            </CatalogControls>

            {isLoading ? (
              <StateCard>
                <StateIcon aria-hidden="true">
                  <FiPackage />
                </StateIcon>

                <StateTitle>Loading safety products...</StateTitle>

                <StateDescription>
                  The product catalogue is being prepared.
                </StateDescription>
              </StateCard>
            ) : errorMessage ? (
              <StateCard>
                <StateIcon aria-hidden="true">
                  <FiShield />
                </StateIcon>

                <StateTitle>Products could not be loaded</StateTitle>

                <StateDescription>{errorMessage}</StateDescription>
              </StateCard>
            ) : filteredProducts?.length === 0 ? (
              <StateCard>
                <StateIcon aria-hidden="true">
                  <FiSearch />
                </StateIcon>

                <StateTitle>No matching products found</StateTitle>

                <StateDescription>
                  Try another search term or select a different category. You
                  can also contact our team for products not shown online.
                </StateDescription>
              </StateCard>
            ) : (
              <>
                <EachProduct filteredProducts={paginatedProducts} />

                <PaginationControls>
                  <div>
                    <PageButton
                      type="button"
                      onClick={() => {
                        setCurrentPage((p) => Math.max(1, p - 1));

                        const el = document.getElementById('product-catalog');

                        if (el) {
                          el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        } else {
                          window.scrollTo({
                            top: 20,
                            behavior: 'smooth',
                          });
                        }
                      }}
                      disabled={currentPage === 1}
                    >
                      ← Prev
                    </PageButton>

                    <PageButton
                      type="button"
                      onClick={() => {
                        setCurrentPage((p) => Math.min(totalPages, p + 1));

                        const el = document.getElementById('product-catalog');

                        if (el) {
                          el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        } else {
                          window.scrollTo({
                            top: 20,
                            behavior: 'smooth',
                          });
                        }
                      }}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </PageButton>

                    <PageInfo>
                      Page {currentPage} of {totalPages}
                    </PageInfo>
                  </div>

                  <div>
                    <label style={{ marginRight: 8 }}>Per page:</label>

                    <PageSizeSelect
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                      <option value={48}>48</option>
                    </PageSizeSelect>
                  </div>
                </PaginationControls>
              </>
            )}
          </CatalogSection>

          <ContactEnd />
        </ExploreProductsPage>
      </MainSection>
    </MainContentContainer>
  );
};

export default ExploreProducts;
